/**
 * SearchUsersUsersList Component
 * Scrollable list of users with infinite scroll pagination, rendered through the shared
 * `VirtualList` (load-more via its `onReachedEnd` sentinel, not a hand-rolled scroll listener).
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart - subscriptionsList
 */

import React, { useRef, useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import {
  useResponsiveDesign,
  useViewportDimensions,
} from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { SearchUsersUserItem } from './SearchUsersUserItem';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { SearchSubscriptionEntity } from '@/domain/entities/subscription/searchSubscriptionEntity';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

export interface SearchUsersUsersListProps {
  subscriptions: SearchSubscriptionEntity[];
  onUserClick: (subscription: ExclusiveContentSubscriptionEntity) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  emptyMessage: string;
}

export const SearchUsersUsersList: React.FC<SearchUsersUsersListProps> = ({
  subscriptions,
  onUserClick,
  onLoadMore,
  hasMore,
  loading,
  emptyMessage,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { height: viewportHeight } = useViewportDimensions();
  const scrollRef = useRef<HTMLDivElement>(null);

  const renderItem = useCallback(
    (row: SearchSubscriptionEntity) => (
      <SearchUsersUserItem
        subscription={row.subscription}
        isSelected={row.isSelected}
        onClick={() => onUserClick(row.subscription)}
      />
    ),
    [onUserClick],
  );

  // Kept referentially stable on purpose: `onLoadMore` resolves to
  // `useSearchUsers.fetchSubscriptions`, a plain arrow re-created on every render, and
  // `VirtualList`'s load-more effect lists `onReachedEnd` in its dep array
  // (VirtualList.tsx:1454). Passing the raw prop tears down and rebuilds the
  // IntersectionObserver every render, and `observe()` re-delivers the current intersection
  // state — so the sentinel would re-fire load-more forever whenever the appended rows are not
  // the rendered rows (an active search: SearchUsersContent.tsx:52 renders `searchResults`
  // while `onLoadMore` appends to `subscriptions`) or a whole page de-duplicates away
  // (useSearchUsers.ts:205-210). With a stable identity the observer is re-created only on an
  // `items.length` change, so the sentinel re-arms once per appended page, not once per render —
  // note (iii) below states the resulting bound. Assigning during render is the pattern
  // `VirtualList` itself uses for `latestUpdateVisibleRangeRef` (VirtualList.tsx:944).
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const handleReachedEnd = useCallback(() => onLoadMoreRef.current(), []);

  if (!loading && !hasMore && subscriptions.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.text.secondary,
          textAlign: 'center',
          padding: responsive.spacing['3xl'],
        }}
      >
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Initial loading state - center the loading indicator
  if (loading && subscriptions.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  return (
    // `scrollbar-hide` hides the scrollbar without disabling scrolling: Flutter paints none here
    // (bare `CustomScrollView` + `BouncingScrollPhysics`, no `Scrollbar` wrapper —
    // search_users_bottom_sheet.dart:510-516), and the sibling sheets do the same
    // (ContentUsersLikedContent.tsx:114-122). Keep `overflowY: 'auto'` — it is what makes this
    // element the scroller BottomSheetView.resolveScroller finds.
    //
    // `position: 'relative'` is visually inert and load-bearing for virtualization:
    // VirtualList.updateVisibleRange resolves `containerTop` from `containerRef.current.offsetTop`
    // for ANY non-window scroller (VirtualList.tsx:850-852), so the list root must have this
    // element as its `offsetParent`. Left `static`, `offsetTop` would resolve against the
    // positioned sheet shell (BottomSheetView.tsx:351, `fixed`/`absolute`) and include the drag
    // handle + search bar + selected-users strip, biasing the render window by that chrome height.
    <div
      ref={scrollRef}
      className="scrollbar-hide"
      {...qaAttr({ id: 'search-users-list-scroll' })}
      style={{
        flex: 1,
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <VirtualList<SearchSubscriptionEntity>
        items={subscriptions}
        renderItem={renderItem}
        getItemKey={(row) => row.subscription.userId}
        // Flutter spaces rows with each row's own padding (search_users_bottom_sheet.dart:735-737),
        // not with an inter-item separator — so the component's 16px default gap is pinned off.
        gap={0}
        scrollContainer={scrollRef}
        // Avatar + name + username row (SearchUsersUserItem.tsx:43-56 → avatar.medium +
        // spacing.lg top/bottom). Budgeting the component's 600px content-card default here
        // would render ~4 of the 9-row first page on the first commit and inflate the scroll
        // extent ~7x until the ResizeObserver catches up. Same shared token
        // LanguageSelectorContent.tsx:163 uses (responsiveDesign.ts:360-369).
        defaultItemHeight={responsive.list.defaultItemHeight}
        qa={{ id: 'search-users-list' }}
        // Ports Flutter's load-more trigger distance (fraction + citation owned by
        // `CONTENT_CONSTANTS.loadMoreViewportFraction`), re-derived on every viewport change exactly
        // as Flutter recomputes it per scroll event. Not VirtualList's invented 200px default.
        // Three deliberate divergences ride on this value:
        // (i)  The IntersectionObserver fires as the sentinel ENTERS the band, i.e. boundary-inclusive
        //      — the analogue of Flutter's loose `<=` (:216) and a correction of the deleted web
        //      listener's strict `<`.
        // (ii) `useViewportDimensions` prefers `visualViewport.height`, which shrinks while a mobile
        //      keyboard is open whereas Flutter's `MediaQuery.size.height` does not — an accepted
        //      narrowing of the band in the very state where the list is half-covered anyway.
        // (iii) Trigger SHAPE differs: this feeds an IntersectionObserver on a bottom sentinel with no
        //      non-reversed overflow short-circuit (VirtualList.tsx:1431-1446), so it also fires
        //      when the list has never been scrolled, where Flutter's scroll-event listener
        //      (:208-219) sits still. ACCEPTED, not a defect: presentation.md mandates VirtualList
        //      and ContentUsersLikedContent.tsx:114-183 is the in-shell precedent (conventions.md →
        //      "Parity is the default — no silent omissions": recorded here instead of silently
        //      inherited). Bounded, not one-shot: the observer is re-created whenever its deps
        //      change (VirtualList.tsx:1454) and `items.length` is the only one that moves here —
        //      every other dep is stable, hence `handleReachedEnd` above rather than the raw
        //      `onLoadMore`, which is what removes the per-render re-arm. Each appended page is
        //      itself an `items.length` change, so the sentinel re-fires once per page while the
        //      region still under-flows (VirtualList.tsx:1399-1403 is explicit: "if new items are
        //      added but the sentinel is still visible … it fires again"), and stops as soon as the
        //      rows overflow the band — typically one page, two on a tall viewport. Beyond that it
        //      is capped by fetchSubscriptions' `!hasMore` bail (useSearchUsers.ts:187,
        //      `if (!currentUserId || isFetchingSubscriptions || !hasMore) return;`, Flutter :222)
        //      — do NOT "fix" it by hand-rolling an overflow guard on the shared component.
        reachedEndThreshold={Math.round(
          viewportHeight * CONTENT_CONSTANTS.loadMoreViewportFraction,
        )}
        {...(hasMore ? { onReachedEnd: handleReachedEnd } : {})}
      />
      {loading && (
        <div style={{ padding: responsive.spacing.base, display: 'flex', justifyContent: 'center' }}>
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
